import React, {FC, Fragment, ReactElement} from 'react';
import {PageHeader} from "../../components/PageHeader";
import {DATE_FORMAT, DATETIME_FORMAT, GUTTER} from "../../constants";
import {SearchField} from "../../components/DataTable/SearchField";
import {Space} from "antd";
import {ExportButton} from "../../components/DataTable/ExportButton";
import {Filter, QueryFilter} from "../../components/DataTable/Filters/types";
import {FilterButton} from "../../components/DataTable/FilterButton";
import {DataTable} from "../../components/DataTable/DataTable";
import {NumericFilter} from "../../components/DataTable/Filters/NumericFilter";
import {ColumnsType} from "antd/lib/table";
import {Student} from "../../types";
import moment from "moment";
import {DateFilter} from "../../components/DataTable/Filters/DateFilter";
import {Translate, useLabels} from "../../hooks/Labels";
import isNumeric from "antd/lib/_util/isNumeric";
import {useOrganisation} from "../../layout/OrganisationInformationProvider";
import {PreserveQueryLink as Link} from "../../components/PreserveQueryLink";
import {OrganisationAnalyticsAccess} from "../../hooks/AnalyticsAccess";

const filters = (canViewIndividualData: boolean): Filter<any>[] => [
    {
        attribute: 'attempts',
        title: 'Attempts',
        component: NumericFilter,
        config: {
            filters: ['exact', 'range', 'lt', 'gt']
        },
    },
    ...(canViewIndividualData ? [{
        attribute: 'average_score',
        title: 'Score',
        component: NumericFilter,
        config: {
            filters: ['exact', 'range', 'lt', 'gt']
        },
    }] : []) as Filter<any>[],
    {
        attribute: 'course_completion',
        title: 'Completion',
        component: NumericFilter,
        config: {
            filters: ['exact', 'range', 'lt', 'gt']
        },
    },
    {
        attribute: 'last_active',
        title: 'Last active',
        component: DateFilter,
        config: {
            filters: ['lt', 'gt', 'range']
        },
    },
    {
        attribute: 'date_joined',
        title: 'Date joined',
        component: DateFilter,
        config: {
            filters: ['lt', 'gt', 'range']
        },
    },
];

const columns = (t: Translate, canViewIndividualData: boolean, canViewUserPii: boolean): ColumnsType<Student> => [
    ...(canViewUserPii ? [{
        title: 'Name',
        dataIndex: 'full_name',
        key: 'full_name',
        render: (text, record) => <Link to={record.id.toString()}>{text}</Link>
    },
    {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        sorter: true
    }] : [{
        title: 'Student',
        dataIndex: 'id',
        key: 'id',
        render: (value, record) => <Link to={record.id.toString()}>{`${t('student')} ${value}`}</Link>
    }]) as ColumnsType<Student>,
    ...(canViewIndividualData ? [{
        title: 'Avg. Score',
        dataIndex: 'average_score',
        key: 'average_score',
        render: (value) => isNumeric(value) ? `${value}%` : 'N/A',
        sorter: true
    }] : []) as ColumnsType<Student>,
    {
        title: 'Completion',
        dataIndex: 'course_completion',
        key: 'course_completion',
        render: (value) => `${value}%`,
        sorter: true
    },
    {
        title: 'Attempts',
        dataIndex: 'attempts',
        key: 'attempts',
        sorter: true
    },
    {
        title: 'Last active',
        dataIndex: 'last_active',
        key: 'last_active',
        sorter: true,
        render: (value) => value ? moment(value).format(DATETIME_FORMAT) : 'N/A'
    },
    {
        title: 'Date joined',
        dataIndex: 'date_joined',
        key: 'date_joined',
        sorter: true,
        render: (value) => value ? moment(value).format(DATE_FORMAT) : 'N/A'
    },
];

export const StudentsPage: FC = (): ReactElement => {
    const {t} = useLabels();
    const {
        can_view_individual_data: canViewIndividualData,
        can_view_user_pii: canViewUserPii
    } = useOrganisation();
    const pageFilters = filters(canViewIndividualData);
    const pageColumns = columns(t, canViewIndividualData, canViewUserPii);

    return (
        <Fragment>
            <PageHeader title={t('student_pl')}/>
            <OrganisationAnalyticsAccess>
                <div>
                <div style={{display: "flex", justifyContent: "space-between", marginBottom: GUTTER[0]}}>
                    {canViewUserPii ? <SearchField placeholder={`${t('student')} name or email`}/> : <div/>}
                    <Space>
                        <ExportButton
                            url='/reports/students/'
                            filenamePrefix={t('student_pl', false)}
                            searchParamFilters={[QueryFilter.COURSES, QueryFilter.COHORTS]}
                        />
                        <FilterButton filters={pageFilters}/>
                    </Space>
                </div>
                <DataTable
                    columns={pageColumns}
                    query='/reports/students/'
                    searchParamFilters={[QueryFilter.COURSES, QueryFilter.COHORTS]}
                />
                </div>
            </OrganisationAnalyticsAccess>
        </Fragment>
    );
};
